import { useContext, useEffect, useState } from "react";

import { ApplicationApiError } from "./api/applicationApi";
import { EmbeddedSessionContext } from "./auth/EmbeddedSessionProvider";
import {
  AccessDeniedState,
  ErrorState,
  LoadingState,
  SessionExpiredState,
  StandaloneState,
} from "./components/ApplicationState";
import { AppRouter } from "./routing/AppRouter";

interface ApplicationContextResponse {
  organization: {
    id: string;
    name: string;
  };
  sageLocationId: string | null;
}

type ContextState =
  | { status: "idle" | "loading" }
  | { status: "ready"; value: ApplicationContextResponse }
  | { status: "denied" | "error"; correlationId?: string };

export function App() {
  const { api, status } = useContext(EmbeddedSessionContext);
  const [contextState, setContextState] = useState<ContextState>({ status: "idle" });

  useEffect(() => {
    if (status !== "authenticated" || !api) {
      setContextState({ status: "idle" });
      return;
    }

    let active = true;
    setContextState({ status: "loading" });
    void api.get<ApplicationContextResponse>("application-context")
      .then((context) => {
        if (active) setContextState({ status: "ready", value: context });
      })
      .catch((error: unknown) => {
        if (!active || (error instanceof ApplicationApiError && error.status === 401)) return;
        if (error instanceof ApplicationApiError && error.status === 403) {
          setContextState({ correlationId: error.correlationId, status: "denied" });
          return;
        }
        setContextState({
          correlationId: error instanceof ApplicationApiError ? error.correlationId : undefined,
          status: "error",
        });
      });

    return () => {
      active = false;
    };
  }, [api, status]);

  if (status === "standalone") return <StandaloneState />;
  if (status === "waiting" || contextState.status === "loading") return <LoadingState />;
  if (status === "expired") return <SessionExpiredState />;
  if (status === "error") return <ErrorState />;
  if (contextState.status === "denied") {
    return <AccessDeniedState correlationId={contextState.correlationId ?? crypto.randomUUID()} />;
  }
  if (contextState.status === "error") return <ErrorState correlationId={contextState.correlationId} />;
  if (contextState.status !== "ready") return <LoadingState />;

  return (
    <AppRouter
      organizationName={contextState.value.organization.name}
      sageLocationId={contextState.value.sageLocationId}
    />
  );
}
